import { Suspense, lazy, useEffect, Component, type ReactNode, type ErrorInfo } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { useVisitorTracking } from "@/hooks/useVisitorTracking";
import { ThemeForcer } from "@/components/ThemeForcer";
import ScrollToTop from "@/components/ScrollToTop";
import LegacyRedirects from "@/components/LegacyRedirects";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { ImpersonationBanner } from "@/components/admin/ImpersonationBanner";

// The service worker only exists in production builds (see vite.config.ts),
// so this virtual module is unresolvable in dev — only import it there.
const PwaUpdatePrompt = import.meta.env.PROD
  ? lazy(() =>
      import("@/components/PwaUpdatePrompt").then((m) => ({ default: m.PwaUpdatePrompt }))
    )
  : null;

// Global Error Boundary to prevent white-screen crashes
class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("ErrorBoundary caught:", error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-background p-6 text-center">
          <h1 className="text-2xl font-semibold text-foreground mb-2">Something went wrong</h1>
          <p className="text-muted-foreground mb-6 max-w-md">
            We hit an unexpected error. You can go back home, or reload and reset cached data if the issue persists.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => { this.setState({ hasError: false }); window.location.href = "/"; }}
              className="px-6 py-2.5 rounded-full bg-primary text-primary-foreground font-medium hover:opacity-90 transition"
            >
              Back to Home
            </button>
            <button
              onClick={() => { window.location.href = "/?reset-app=1"; }}
              className="px-6 py-2.5 rounded-full border border-border text-foreground font-medium hover:bg-muted transition"
            >
              Reload &amp; reset
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
// Critical path - load immediately for homepage
import Index from "./pages/Index";

// Lazy load all other routes for smaller initial bundle
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const VerifyEmail = lazyWithRetry(() => import("./pages/auth/VerifyEmail"));
const AuthCallback = lazyWithRetry(() => import("./pages/auth/AuthCallback"));
const VendorSetupPassword = lazyWithRetry(() => import("./pages/auth/VendorSetupPassword"));
const VendorRegister = lazyWithRetry(() => import("./pages/VendorRegister"));
const VendorTerms = lazyWithRetry(() => import("./pages/VendorTerms"));
const VendorApplication = lazyWithRetry(() => import("./pages/VendorApplication"));
const Shop = lazyWithRetry(() => import("./pages/Shop"));
const CategoryPage = lazyWithRetry(() => import("./pages/CategoryPage"));
const Product = lazyWithRetry(() => import("./pages/Product"));
const Cart = lazyWithRetry(() => import("./pages/Cart"));
const Checkout = lazyWithRetry(() => import("./pages/Checkout"));
const PaymentSuccess = lazyWithRetry(() => import("./pages/PaymentSuccess"));
const PaymentCancelled = lazyWithRetry(() => import("./pages/PaymentCancelled"));
const Blog = lazyWithRetry(() => import("./pages/Blog"));
const BlogPost = lazyWithRetry(() => import("./pages/BlogPost"));
const About = lazyWithRetry(() => import("./pages/About"));
const Vendors = lazyWithRetry(() => import("./pages/Vendors"));
const Orders = lazyWithRetry(() => import("./pages/Orders"));
const Wishlist = lazyWithRetry(() => import("./pages/Wishlist"));
const Account = lazyWithRetry(() => import("./pages/Account"));
const VendorStorefront = lazyWithRetry(() => import("./pages/VendorStorefront"));
const Community = lazyWithRetry(() => import("./pages/Community"));
const UploadFit = lazyWithRetry(() => import("./pages/UploadFit"));
const UserProfile = lazyWithRetry(() => import("./pages/UserProfile"));
const Looks = lazyWithRetry(() => import("./pages/Looks"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
const TermsAndConditions = lazyWithRetry(() => import("./pages/TermsAndConditions"));
const CookiePolicy = lazyWithRetry(() => import("./pages/CookiePolicy"));
const SizeGuide = lazyWithRetry(() => import("./pages/SizeGuide"));
const Returns = lazyWithRetry(() => import("./pages/Returns"));
const Shipping = lazyWithRetry(() => import("./pages/Shipping"));
const Contact = lazyWithRetry(() => import("./pages/Contact"));
const FAQ = lazyWithRetry(() => import("./pages/FAQ"));
const Install = lazyWithRetry(() => import("./pages/Install"));
const HijabQuiz = lazyWithRetry(() => import("./pages/HijabQuiz"));
const GiftCards = lazyWithRetry(() => import("./pages/GiftCards"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const GuestOrderLookup = lazyWithRetry(() => import("./pages/GuestOrderLookup"));
const TileQA = lazyWithRetry(() => import("./pages/TileQA"));
const CustomerReturns = lazyWithRetry(() => import("./pages/CustomerReturns"));
const LandingPage = lazyWithRetry(() => import("./pages/landing/LandingPage"));
const LegacyGonePage = lazyWithRetry(() => import("./pages/LegacyGonePage"));
import { LANDING_PAGES } from "@/data/landingPages";

// Lazy load Admin routes
const AdminLayout = lazyWithRetry(() => import("./components/admin/AdminLayout"));
const AdminDashboard = lazyWithRetry(() => import("./pages/admin/AdminDashboard"));
const AdminVendors = lazyWithRetry(() => import("./pages/admin/AdminVendors"));
const AdminUsers = lazyWithRetry(() => import("./pages/admin/AdminUsers"));
const AdminOrders = lazyWithRetry(() => import("./pages/admin/AdminOrders"));
const AdminAnalytics = lazyWithRetry(() => import("./pages/admin/AdminAnalytics"));
const AdminSettings = lazyWithRetry(() => import("./pages/admin/AdminSettings"));

const AdminConnectTransfers = lazyWithRetry(() => import("./pages/admin/AdminConnectTransfers"));
const AdminNewsletter = lazyWithRetry(() => import("./pages/admin/AdminNewsletter"));
const AdminCommunity = lazyWithRetry(() => import("./pages/admin/AdminCommunity"));
const AdminSmsCampaigns = lazyWithRetry(() => import("./pages/admin/AdminSmsCampaigns"));
const AdminReports = lazyWithRetry(() => import("./pages/admin/AdminReports"));
const AdminContact = lazyWithRetry(() => import("./pages/admin/AdminContact"));
const AdminShipping = lazyWithRetry(() => import("./pages/admin/AdminShipping"));
const AdminWallets = lazyWithRetry(() => import("./pages/admin/AdminWallets"));
const AdminCategories = lazyWithRetry(() => import("./pages/admin/AdminCategories"));
const AdminFeaturedCollections = lazyWithRetry(() => import("./pages/admin/AdminFeaturedCollections"));
const AdminAdvertisements = lazyWithRetry(() => import("./pages/admin/AdminAdvertisements"));
const AdminProducts = lazyWithRetry(() => import("./pages/admin/AdminProducts"));
const AdminSEO = lazyWithRetry(() => import("./pages/admin/AdminSEO"));
const AdminProductImport = lazyWithRetry(() => import("./pages/admin/AdminProductImport"));

// Lazy load Vendor routes
const VendorLayout = lazyWithRetry(() => import("./components/vendor/VendorLayout"));
const VendorDashboard = lazyWithRetry(() => import("./pages/vendor/VendorDashboard"));
const VendorProducts = lazyWithRetry(() => import("./pages/vendor/VendorProducts"));
const VendorDrafts = lazyWithRetry(() => import("./pages/vendor/VendorDrafts"));
const ProductEditor = lazyWithRetry(() => import("./pages/vendor/ProductEditor"));
const VendorOrders = lazyWithRetry(() => import("./pages/vendor/VendorOrders"));
const VendorAnalytics = lazyWithRetry(() => import("./pages/vendor/VendorAnalytics"));
const VendorStore = lazyWithRetry(() => import("./pages/vendor/VendorStore"));
const VendorFinancials = lazyWithRetry(() => import("./pages/vendor/VendorFinancials"));
const VendorSettings = lazyWithRetry(() => import("./pages/vendor/VendorSettings"));
const VendorReviews = lazyWithRetry(() => import("./pages/vendor/VendorReviews"));
const VendorInventory = lazyWithRetry(() => import("./pages/vendor/VendorInventory"));

const VendorBilling = lazyWithRetry(() => import("./pages/vendor/VendorBilling"));
const VendorReturns = lazyWithRetry(() => import("./pages/vendor/VendorReturns"));
const VendorFeaturedCollections = lazyWithRetry(() => import("./pages/vendor/VendorFeaturedCollections"));
const AdminReturns = lazyWithRetry(() => import("./pages/admin/AdminReturns"));


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2,
      gcTime: 1000 * 60 * 10,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Minimal loading fallback - just a subtle pulse
const RouteLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
  </div>
);

// Wrapper component to use hooks inside Router context
function AppRoutes() {
  useVisitorTracking();
  

  useEffect(() => {
    const handler = (event: PromiseRejectionEvent) => {
      console.error("Unhandled rejection:", event.reason);
      event.preventDefault();
    };
    window.addEventListener("unhandledrejection", handler);
    return () => window.removeEventListener("unhandledrejection", handler);
  }, []);

  // Strip legacy ?mobile=1 query param (WordPress mobile plugin remnant)
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has("mobile")) {
      url.searchParams.delete("mobile");
      const newUrl = url.pathname + (url.searchParams.toString() ? `?${url.searchParams}` : "") + url.hash;
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  return (
    <>
      <ImpersonationBanner />
      <LegacyRedirects />
      <ThemeForcer />
      <Suspense fallback={<RouteLoader />}>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/auth/verify-email" element={<VerifyEmail />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/vendor-setup" element={<VendorSetupPassword />} />
        <Route path="/login" element={<Auth />} />
        <Route path="/register" element={<Auth />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/product/:slug" element={<Product />} />
        {/* Vendor preview of an unpublished product. Deliberately renders the
            real product page rather than a lookalike, so it cannot drift from
            what customers actually see. Access is enforced by RLS — only the
            owning vendor (and admins) can read a non-public product — and the
            page redirects to the public URL once the product is live.

            MUST live outside /vendor: getPortalTheme() forces the dark portal
            theme on every /vendor/* route, which themed the storefront preview
            as the vendor dashboard — light text on the marketplace's light
            background. The preview is a storefront page and has to be themed
            like one. */}
        <Route path="/preview/product/:productId" element={<Product preview />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/payment-cancelled" element={<PaymentCancelled />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/about" element={<About />} />
        <Route path="/vendors" element={<Vendors />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="/wishlist" element={<Wishlist />} />
        <Route path="/account" element={<Account />} />
        <Route path="/account/returns" element={<CustomerReturns />} />
        <Route path="/store/:vendorId" element={<VendorStorefront />} />
        <Route path="/community" element={<Community />} />
        <Route path="/upload-fit" element={<UploadFit />} />
        <Route path="/profile/:userId" element={<UserProfile />} />
        <Route path="/looks" element={<Looks />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/cookies" element={<CookiePolicy />} />
        <Route path="/size-guide" element={<SizeGuide />} />
        <Route path="/returns" element={<Returns />} />
        <Route path="/shipping" element={<Shipping />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/install" element={<Install />} />
        <Route path="/track-order" element={<GuestOrderLookup />} />
        <Route path="/qa/tiles" element={<TileQA />} />
        <Route path="/quiz" element={<HijabQuiz />} />
        <Route path="/gift-cards" element={<GiftCards />} />
        <Route path="/vendor/register" element={<VendorRegister />} />
        <Route path="/vendor/terms" element={<VendorTerms />} />
        <Route path="/vendor/application" element={<VendorApplication />} />

        {/* Vendor Portal Routes */}
        <Route path="/vendor" element={<VendorLayout />}>
          <Route index element={<VendorDashboard />} />
          <Route path="products" element={<VendorProducts />} />
          <Route path="products/new" element={<ProductEditor />} />
          <Route path="products/:productId" element={<ProductEditor />} />
          <Route path="drafts" element={<VendorDrafts />} />
          <Route path="inventory" element={<VendorInventory />} />
          
          <Route path="orders" element={<VendorOrders />} />
          <Route path="analytics" element={<VendorAnalytics />} />
          <Route path="store" element={<VendorStore />} />
          <Route path="reviews" element={<VendorReviews />} />
          <Route path="billing" element={<VendorBilling />} />
          <Route path="financials" element={<VendorFinancials />} />
          <Route path="returns" element={<VendorReturns />} />
          <Route path="featured-collections" element={<VendorFeaturedCollections />} />
          <Route path="settings" element={<VendorSettings />} />
        </Route>

        {/* Admin Routes */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="vendors" element={<AdminVendors />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="shipping" element={<AdminShipping />} />
          <Route path="wallets" element={<AdminWallets />} />
          
          <Route path="transfers" element={<AdminConnectTransfers />} />
          <Route path="newsletter" element={<AdminNewsletter />} />
          <Route path="community" element={<AdminCommunity />} />
          <Route path="contact" element={<AdminContact />} />
          <Route path="sms-campaigns" element={<AdminSmsCampaigns />} />
          <Route path="reports" element={<AdminReports />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="categories" element={<AdminCategories />} />
          <Route path="featured-collections" element={<AdminFeaturedCollections />} />
          <Route path="advertisements" element={<AdminAdvertisements />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="seo" element={<AdminSEO />} />
          <Route path="import-products" element={<AdminProductImport />} />
          <Route path="returns" element={<AdminReturns />} />
          <Route path="settings" element={<AdminSettings />} />
        </Route>

        <Route path="/products" element={<Navigate to="/shop" replace />} />
        <Route path="/products/:id" element={<Navigate to="/shop" replace />} />

        {/* SEO Landing Pages — must come before category wildcard */}
        {LANDING_PAGES.map((p) => (
          <Route key={p.slug} path={`/${p.slug}`} element={<LandingPage slug={p.slug} />} />
        ))}

        {/* Legacy German WooCommerce + WordPress paths — render Gone page with noindex */}
        <Route path="/produkte" element={<LegacyGonePage />} />
        <Route path="/produkte/*" element={<LegacyGonePage />} />
        <Route path="/impressum" element={<LegacyGonePage />} />
        <Route path="/kontakt" element={<LegacyGonePage />} />
        <Route path="/ueber-uns" element={<LegacyGonePage />} />
        <Route path="/versand" element={<LegacyGonePage />} />
        <Route path="/datenschutz" element={<LegacyGonePage />} />
        <Route path="/widerrufsbelehrung" element={<LegacyGonePage />} />
        <Route path="/wp-admin" element={<LegacyGonePage />} />
        <Route path="/wp-admin/*" element={<LegacyGonePage />} />
        <Route path="/wp-login.php" element={<LegacyGonePage />} />
        <Route path="/xmlrpc.php" element={<LegacyGonePage />} />
        <Route path="/wp-content/*" element={<LegacyGonePage />} />
        <Route path="/wp-includes/*" element={<LegacyGonePage />} />

        <Route path="/:categorySlug/*" element={<CategoryPage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
    </>
  );
}

const App = () => (
  <ErrorBoundary>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <CartProvider>
            <TooltipProvider>
              <BrowserRouter>
                <Toaster />
                <Sonner />
                {PwaUpdatePrompt && (
                  <Suspense fallback={null}>
                    <PwaUpdatePrompt />
                  </Suspense>
                )}
                <ScrollToTop />
                <AppRoutes />
              </BrowserRouter>
            </TooltipProvider>
          </CartProvider>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  </ErrorBoundary>
);

export default App;
